import { z } from 'zod'
import { fallback } from '../../../utilities/zod-utils'
import {
	colorSchema,
	metaGeneralContentDefault,
	metaGeneralContentSchema,
	metaOverlayDefault,
	metaOverlaySchema,
} from './common'
import {
	defaultSubtitleTextLayerConfiguration,
	defaultTitleTextLayerConfiguration,
	metaDefaultSubtitleTextLayerSchema,
	metaDefaultTitleTextLayerSchema,
} from './common/meta-text-layer'
import { metaTransformDefault, metaTransformSchema } from './common/meta-transform'
import { storyAdPositioningDefault, storyAdPositioningSchema } from './story-ad-positioning'

export type UnconformedCampaignStoryAd = {
	// The id of the ad
	id: string
	// The title of the story
	title: string
	// The subtitle of the story
	subtitle: string
	// The shown location
	location: string
	// The CTA text
	ctaText: string
	// The background image
	imageId: string
	// narrow this using the schema
	meta: unknown
}

export const storyAdDefaultMeta = {
	textColor: 'white',
	logoBackgroundFillColor: '#ffffff',
	metaOverlay: metaOverlayDefault,
	backdropOverlay: metaOverlayDefault,
	backdropOverlayProMode: false,
	backdropTransform: {
		...metaTransformDefault,
		offset: { x: 0, y: 0 },
		scale: 1,
	},
	titleTextLayer: defaultTitleTextLayerConfiguration,
	titleTextTransform: metaTransformDefault,
	subtitleTextLayer: defaultSubtitleTextLayerConfiguration,
	subtitleTextTransform: metaTransformDefault,
	generalContent: metaGeneralContentDefault,
	positioning: storyAdPositioningDefault,
	canvasEnabled: false,
	canvasDocument: null,
	forceAiGeneratedLabel: false,
} as const

export const storyAdMetaSchema = z.object({
	textColor: z.enum(['white', 'black', 'foreground']).or(fallback('white' as const)),
	logoBackgroundFillColor: colorSchema.or(fallback('#ffffff')),
	metaOverlay: metaOverlaySchema,
	backdropOverlay: metaOverlaySchema,
	backdropOverlayProMode: z.boolean().or(fallback(false)),
	backdropTransform: metaTransformSchema,
	titleTextLayer: metaDefaultTitleTextLayerSchema,
	titleTextTransform: metaTransformSchema,
	subtitleTextLayer: metaDefaultSubtitleTextLayerSchema,
	subtitleTextTransform: metaTransformSchema,
	generalContent: metaGeneralContentSchema,
	positioning: storyAdPositioningSchema,
	canvasEnabled: z.boolean().or(fallback(false)),
	canvasDocument: z.string().nullable().or(fallback(null)),
	// Shows the label even when no image is detected as AI-generated, e.g. for externally generated uploads.
	forceAiGeneratedLabel: z.boolean().or(fallback(false)),
})

export type StoryAdMeta = z.infer<typeof storyAdMetaSchema>

export type ConformedCampaignStoryAd = Omit<UnconformedCampaignStoryAd, 'meta'> & {
	meta: StoryAdMeta
}

export function conformStoryAd(storyAd: UnconformedCampaignStoryAd): ConformedCampaignStoryAd {
	const meta = storyAd.meta

	// safeguard for non-object input
	if (!meta || typeof meta !== 'object' || Array.isArray(meta) || Object.keys(meta).length === 0) {
		return {
			...storyAd,
			meta: storyAdDefaultMeta,
		}
	}

	return {
		...storyAd,
		meta: storyAdMetaSchema.parse(storyAd.meta),
	}
}
